import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, ChevronUp, ArrowRight, ArrowLeft, Repeat,
  TrendingDown, ShoppingCart,
} from 'lucide-react';
import axios from 'axios';
import { RideCard } from './RideCard';
import { API } from '../utils/apiConfig';

/**
 * RoundTripBundle — Cash-Flow Sprint
 * Groups a forward + return ride sharing a `return_of_ride_id` relation.
 * Renders a compact bundle card with VISIBLE-WITHOUT-EXPAND savings badge
 * and a "Beide buchen" CTA that calls POST /api/bookings/round-trip.
 *
 * Props:
 *   forwardRide  — the outbound ride
 *   returnRide   — the sibling return ride
 *   onClick(ride) — RideCard click (single-ride detail modal)
 *   onViewRoute(ride)
 *   onBookBundle({ outboundId, returnId, totalPrice, savingsPct }) — Cash-Flow CTA
 *   bundleLoading — boolean (parent disables CTA while in flight)
 */
export const RoundTripBundle = ({
  forwardRide, returnRide,
  onClick, onViewRoute,
  onBookBundle, bundleLoading,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);

  const outPrice = Number(forwardRide.price_per_seat || forwardRide.calculated_price || 0);
  const retPrice = Number(returnRide.price_per_seat || returnRide.calculated_price || 0);
  const totalPrice = (outPrice + retPrice).toFixed(2);

  const outKm = Number(forwardRide.route_distance_km || forwardRide.distance_km || 0);
  const retKm = Number(returnRide.route_distance_km || returnRide.distance_km || 0);
  const totalKm = outKm + retKm;

  // Aggregated savings vs flat 15% — single /pricing/quote on combined leg
  useEffect(() => {
    let alive = true;
    const fetchAggregateQuote = async () => {
      if (!totalPrice || !totalKm) return;
      try {
        // Two parallel quotes for the two legs, then aggregate
        const [oQ, rQ] = await Promise.all([
          axios.get(`${API}/pricing/quote`, {
            params: { ride_price: outPrice, distance_km: outKm || 100 },
          }).catch(() => null),
          axios.get(`${API}/pricing/quote`, {
            params: { ride_price: retPrice, distance_km: retKm || 100 },
          }).catch(() => null),
        ]);
        if (!alive || !oQ || !rQ) return;
        const oFee = Number(oQ.data.service_fee || 0);
        const rFee = Number(rQ.data.service_fee || 0);
        const flat15 = (outPrice + retPrice) * 0.15;
        const actualNet = oFee + rFee;
        const savedPct = flat15 > 0
          ? Math.round(Math.max(0, (flat15 - actualNet) / flat15) * 100)
          : 0;
        setQuote({
          outBd: oQ.data, retBd: rQ.data,
          totalFeeNet: actualNet,
          totalFeeGross: Number(oQ.data.service_fee_gross || 0)
                       + Number(rQ.data.service_fee_gross || 0),
          totalPrice:    Number(oQ.data.total || 0)
                       + Number(rQ.data.total || 0),
          savingsPct: savedPct,
        });
      } catch { /* swallow — badge just hides */ }
    };
    fetchAggregateQuote();
    return () => { alive = false; };
  }, [outPrice, retPrice, outKm, retKm, totalPrice, totalKm]);

  const handleBundleClick = (e) => {
    e.stopPropagation();
    if (bundleLoading || !onBookBundle) return;
    onBookBundle({
      outboundId: forwardRide.ride_id || forwardRide.id,
      returnId: returnRide.ride_id || returnRide.id,
      totalPrice: quote ? quote.totalPrice : Number(totalPrice),
      savingsPct: quote ? quote.savingsPct : 0,
      forwardRide, returnRide,
    });
  };

  const savingsPct = quote ? quote.savingsPct : 0;
  const showBadge = savingsPct > 0;

  return (
    <div
      data-testid={`round-trip-bundle-${forwardRide.ride_id || forwardRide.id}`}
      style={{
        background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
        border: '1.5px solid #c4b5fd',
        borderRadius: 14,
        marginBottom: 18,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(167,139,250,0.18)',
      }}
    >
      <button
        type="button"
        data-testid={`round-trip-bundle-toggle-${forwardRide.ride_id || forwardRide.id}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', padding: '12px 16px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
          color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flex: '0 0 auto',
        }}>
          <Repeat size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 14, color: '#0f172a',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span>
              {t('bundle.title')}: {forwardRide.start_city} ↔ {forwardRide.end_city || forwardRide.destination_city}
            </span>
            {/* SAVINGS BADGE in HEADER — visible WITHOUT expanding */}
            {showBadge && (
              <span
                data-testid={`bundle-savings-badge-${forwardRide.ride_id || forwardRide.id}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '2px 9px', borderRadius: 999,
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.20), rgba(22,163,74,0.20))',
                  border: '1px solid rgba(34,197,94,0.50)',
                  color: '#15803d', fontSize: 11, fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                <TrendingDown size={11} />
                −{savingsPct}% {t('bundle.youSave')}
              </span>
            )}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: '#7c3aed', marginTop: 4,
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <ArrowRight size={11} /> {forwardRide.departure_time || '—'}
            </span>
            <span>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <ArrowLeft size={11} /> {returnRide.departure_time || '—'}
            </span>
            <span>·</span>
            <strong>{totalPrice} €</strong>
            <span style={{ color: '#94a3b8' }}>
              {t('bundle.combined')}
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={18} color="#7c3aed" /> : <ChevronDown size={18} color="#7c3aed" />}
      </button>

      {/* CTA — visible WITHOUT expand (Cash-Flow conversion accelerator) */}
      <div style={{
        padding: '0 16px 12px',
        display: 'flex', justifyContent: 'flex-end',
      }}>
        <button
          type="button"
          data-testid={`bundle-book-cta-${forwardRide.ride_id || forwardRide.id}`}
          onClick={handleBundleClick}
          disabled={bundleLoading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 10,
            background: bundleLoading
              ? '#cbd5e1'
              : 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
            color: 'white', border: 'none',
            fontSize: 13, fontWeight: 700,
            cursor: bundleLoading ? 'wait' : 'pointer',
            boxShadow: bundleLoading ? 'none' : '0 4px 12px rgba(124,58,237,0.35)',
            letterSpacing: 0.2,
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (!bundleLoading) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(124,58,237,0.45)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = bundleLoading
              ? 'none'
              : '0 4px 12px rgba(124,58,237,0.35)';
          }}
        >
          <ShoppingCart size={14} />
          {bundleLoading
            ? t('bundle.booking')
            : t('bundle.bookBoth')}
        </button>
      </div>

      {open && (
        <div style={{ padding: '4px 12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div data-testid="round-trip-bundle-leg-forward">
            <div style={legLabel}>
              <ArrowRight size={12} /> {t('bundle.outbound')}
            </div>
            <RideCard ride={forwardRide} onClick={() => onClick(forwardRide)} onViewRoute={onViewRoute} />
          </div>
          <div data-testid="round-trip-bundle-leg-return">
            <div style={legLabel}>
              <ArrowLeft size={12} /> {t('bundle.return')}
            </div>
            <RideCard ride={returnRide} onClick={() => onClick(returnRide)} onViewRoute={onViewRoute} />
          </div>
        </div>
      )}
    </div>
  );
};

const legLabel = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 9px',
  background: '#fff',
  border: '1px solid #e9d5ff',
  borderRadius: 999,
  fontSize: 11, fontWeight: 700, color: '#7c3aed',
  marginBottom: 4, marginLeft: 4,
};

/**
 * Helper: split a flat rides array into [solo rides, [forward,return] pairs].
 *
 * Recognises pairs in TWO ways:
 *   1. Inline back-link:  ride.linked_return_ride is the embedded return leg
 *      (driver-facing search response — most common case).
 *   2. In-list back-link: a sibling ride within the same array carries
 *      return_of_ride_id pointing at another ride in the array (legacy /
 *      driver dashboard cases).
 *
 * Pure — no side-effects. DRY entry-point used by RideSearch.js.
 */
export const groupRoundTrips = (rides) => {
  if (!Array.isArray(rides) || rides.length === 0) return { solo: [], pairs: [] };
  const byId = new Map();
  rides.forEach((r) => byId.set(r.ride_id || r.id, r));

  const used = new Set();
  const pairs = [];

  // Pass 1 — inline `linked_return_ride` (server-enriched bundles)
  rides.forEach((r) => {
    const myId = r.ride_id || r.id;
    if (used.has(myId)) return;
    if (r.linked_return_ride) {
      const ret = r.linked_return_ride;
      const retId = ret.ride_id || ret.id;
      pairs.push({ forward: r, return: ret });
      used.add(myId);
      if (retId) used.add(retId);
    }
  });

  // Pass 2 — in-list pairing via return_of_ride_id (driver-side or
  // multi-leg search where both rides happen to land in the same result).
  rides.forEach((r) => {
    const myId = r.ride_id || r.id;
    if (used.has(myId)) return;
    const returnOf = r.return_of_ride_id;
    if (returnOf && byId.has(returnOf) && !used.has(returnOf)) {
      pairs.push({ forward: byId.get(returnOf), return: r });
      used.add(myId);
      used.add(returnOf);
    }
  });

  const solo = rides.filter((r) => !used.has(r.ride_id || r.id));
  return { solo, pairs };
};

export default RoundTripBundle;
